/**
 * One button, built the same way everywhere.
 *
 * Hero, CTA band and the standalone Button block all render the same thing, and before this
 * existed each one carried its own copy of the size list. The presets drifted from the
 * primitive within a day — the Button block grew sizes that the Hero's button did not have,
 * which is the exact complaint that produced this file.
 *
 * Blocks now declare `...buttonStyleFields()` and call `buttonNode()`. Adding a size here
 * adds it everywhere.
 */

import { el, text as textNode, type CtaValue, type FieldSpec, type IRNode } from '@lp/shared';
import {
  BUTTON_RADIUS_OPTIONS,
  BUTTON_SIZE_OPTIONS,
  BUTTON_STYLE_DEFAULTS,
  ctaEmbedHtml,
  readButtonStyle,
  type ButtonStyle,
} from './ctaEmbed.js';
import { textCls } from './util.js';

/** The fields every block with a button exposes. Spread into a block's `fields`. */
export function buttonStyleFields(prefix = ''): Record<string, FieldSpec> {
  const key = (name: string): string => (prefix ? `${prefix}${name[0]!.toUpperCase()}${name.slice(1)}` : name);
  return {
    [key('size')]: {
      type: 'select',
      label: 'Button size',
      help: 'Medium is the button used across the live site.',
      options: [...BUTTON_SIZE_OPTIONS],
    },
    [key('radius')]: {
      type: 'radio',
      label: 'Button corners',
      options: [...BUTTON_RADIUS_OPTIONS],
    },
    [key('fullWidth')]: {
      type: 'boolean',
      label: 'Full width button',
      help: 'Stretches to the column. Usually what you want on a phone.',
    },
  };
}

/** The matching default props. */
export function buttonStyleDefaults(prefix = ''): Record<string, unknown> {
  const key = (name: string): string => (prefix ? `${prefix}${name[0]!.toUpperCase()}${name.slice(1)}` : name);
  return {
    [key('size')]: BUTTON_STYLE_DEFAULTS.size,
    [key('radius')]: BUTTON_STYLE_DEFAULTS.radius,
    [key('fullWidth')]: BUTTON_STYLE_DEFAULTS.fullWidth,
  };
}

/** Pull a `ButtonStyle` back out of a block's flat props. */
export function buttonStyleFrom(props: Record<string, unknown>, prefix = ''): ButtonStyle {
  const key = (name: string): string => (prefix ? `${prefix}${name[0]!.toUpperCase()}${name.slice(1)}` : name);
  return readButtonStyle({
    size: props[key('size')] as ButtonStyle['size'],
    radius: props[key('radius')] as ButtonStyle['radius'],
    fullWidth: props[key('fullWidth')] as boolean,
  });
}

/**
 * The button itself.
 *
 * Primary is reproduced verbatim from the live site — a raw inline-styled `<button>` in a
 * `w-embed w-script` wired to `handleConversionClick`. PostHog and Mixpanel bind to exactly
 * that shape, so a tidier equivalent would look identical and silently stop reporting
 * conversions. Secondary is a normal link with our own classes, because it has no analytics
 * contract to honour.
 */
export function buttonNode(
  cta: CtaValue,
  variant: 'primary' | 'secondary',
  style: ButtonStyle,
): IRNode {
  if (variant === 'primary') {
    return {
      kind: 'embed',
      html: ctaEmbedHtml(cta, style),
      cls: [
        { name: 'lp-cta-embed' },
        ...(style.fullWidth
          ? [{ name: 'is-cta-embed-full', combo: true, parent: 'lp-cta-embed' } as const]
          : []),
      ],
    };
  }

  return el(
    'Link',
    'a',
    textCls(
      'lp-cta-secondary',
      // `medium` and `pill` are the base class, so they contribute no combo.
      style.size !== 'medium' ? `is-btn-${style.size}` : null,
      style.radius !== 'pill' ? `is-btn-${style.radius}` : null,
      style.fullWidth ? 'is-btn-full' : null,
    ),
    [textNode(cta.label)],
    { attr: { href: cta.href } },
  );
}
